import * as React from 'react';
import { db } from '../lib/firebaseConfig';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import Cookies from 'universal-cookie';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import NotificationsIcon from '@mui/icons-material/Notifications';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import './BadgeNotification.css';

const truncate = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
};

export default function ColorBadge() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [messageCount, setMessageCount] = React.useState(0);
  const [notifications, setNotifications] = React.useState([]); // State to store notifications
  const [isHovered, setIsHovered] = React.useState(false); // State to track hover state
  const cookies = new Cookies();
  const userID = cookies.get('uuid');

  React.useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'chatUsers', userID, 'messages'), orderBy('timestamp')),
      (snapshot) => {
        const newNotifications = snapshot.docs.map((doc) => ({
          message: truncate(doc.data().message, 30),
          senderName: doc.data().name, // Change the field to "name"
          timestamp: doc.data().timestamp.toDate(),
        }));
        setNotifications(newNotifications);
        setMessageCount(newNotifications.length);
      }
    );

    return () => unsubscribe();
  }, [userID]);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formatDate = (timestamp) => {
    const today = new Date();
    const messageDate = timestamp.toDateString();
    if (today.toDateString() === messageDate) {
      return formatDistanceToNow(timestamp, { addSuffix: true });
    } else {
      return `${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`;
    }
  };

  const markAllAsRead = () => {
    // Perform the necessary actions to mark all notifications as read
    setNotifications([]);
    setMessageCount(0);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Stack spacing={2} direction="row" style={{ margin: 'auto 20px' }}>
      <Badge badgeContent={messageCount} color="error" onClick={handlePopoverOpen} className="Badge">
        <NotificationsIcon sx={{ color: 'white' }} />
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            backgroundColor: '#171A26',
            borderRadius: '5px',
            padding: '0px',
            maxHeight: '300px',
            marginTop: '15px',
            overflow: 'auto',
            maxWidth: '250px',
            backdropFilter: 'blur(10px)', // Add blur to the popover
            opacity: 1, // Add transparency to the popover
          },
        }}
      >
        <Box>
          <List dense sx={{ color: 'white' }}>
            {notifications.length === 0 ? (
              <ListItem
                disablePadding
                sx={{
                  borderRadius: '0px',
                  backgroundColor: '#313648',
                  padding: '10px',
                  paddingBottom: '0px',
                  paddingTop: '0px',
                  textAlign: 'center',
                }}
              >
                <ListItemText primary="No notifications" />
              </ListItem>
            ) : (
              <>
                <ListItem
                  disablePadding
                  sx={{
                    borderRadius: '0px',
                    backgroundColor: '#313648',
                    padding: '10px',
                    paddingBottom: '0px',
                    paddingTop: '0px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#313648', cursor: 'pointer' },
                  }}
                  onClick={markAllAsRead}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <ListItemText primary="Mark all as read" />
                  <IconButton
                    size="small"
                    style={{
                      marginLeft: '4px',
                      visibility: isHovered ? 'visible' : 'hidden',
                    }}
                  >
                    <DeleteIcon sx={{ color: 'white', width: '20px' }} />
                  </IconButton>
                </ListItem>
                <Divider sx={{ background: '#424242' }} />
                {notifications.map((notification, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      disablePadding
                      sx={{
                        borderRadius: '0px',
                        padding: '10px',
                        '&:hover': { backgroundColor: '#313648', cursor: 'pointer' },
                      }}
                    >
                      <ListItemText
                        primary={notification.message}
                        secondary={formatDate(notification.timestamp)}
                        className='timestamp'
                      />
                    </ListItem>
                    {index !== notifications.length - 1 && (
                      <Divider sx={{ background: '#424242' }} />
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </List>
        </Box>
      </Popover>
    </Stack>
  );
}
